
import { OrganizationUpdateData, prepareOrganizationUpdateData, transformOrganizationData } from '../helpers';
import { BaseAPIError, Id, KeyvisualList, ListQuery, ListState, Organization, OrganizationListFilter, OrganizationRole, OrganizationTransformed, OrganizationType, OrganizationUpdateRoute, OrganizationWithoutRelations } from '../types';
import { abibApi, tags, methods } from './';

// type OrganizationWithoutRelations = Omit<Organization, 'associations' | 'contacts' | 'languages' | 'media' | 'news' | 'organization_type' | 'productDetails' | 'rating_data' | 'rating_data' | 'roles' | 'sectors'>;

const apiOrganization = abibApi.injectEndpoints({
  endpoints: (builder) => ({
    getAllOrganizationTypes: builder.query<{ items: OrganizationType[] }, any>({
      query: (arg) => {
        return {
          url: 'orgs/orgtypes/simplified',
          method: methods.GET,
        };
      },
      providesTags: (result, error, params) => [tags.organizationTypes],
      transformResponse: ({ data }, meta, arg) => ({
        items: data as OrganizationType[],
      }),
    }),
    getOrganizationList: builder.query<ListState<Organization, OrganizationListFilter>, ListQuery<OrganizationListFilter>>({
      query: ({ page, perPage, filter }) => {
        return {
          method: methods.GET,
          url: `/orgs?page=${page || 1}&perPage=${perPage || 10}&filter=${JSON.stringify(filter || {})}`,
        };
      },
      providesTags: (result, error, params) => [tags.organizationList],
      transformResponse: ({ data, meta: { links, path, ...pagination } }, meta, { filter }) => ({
        filter: filter,
        items: data,
        pagination: pagination,
      }),
    }),
    getOrganization: builder.query<Organization, { org_id?: Id, org_name?: string }>({
      query: ({ org_id, org_name }) => {
        if (org_id) {
          return {
            method: methods.GET,
            url: `/orgs/${org_id}`,
          };
        }
        if (org_name) {
          return {
            method: methods.GET,
            url: `/orgByName/${org_name}`,
          };
        }
        return {}
        // return Promise.reject(new Error('org_id or org_name is required'));
      },
      providesTags: (result, error, { org_id, org_name }) => [{ type: tags.organization, org_id: result?.id }],
      transformResponse: ({ data }, meta, arg) => data,
    }),
    getOrganizationTransformed: builder.query<OrganizationTransformed, { org_id?: Id, org_name?: string }>({
      query: ({ org_id, org_name }) => {
        if (org_id) {
          return {
            method: methods.GET,
            url: `/orgs/${org_id}`,
          };
        }
        if (org_name) {
          return {
            method: methods.GET,
            url: `/orgByName/${org_name}`,
          };
        }
        return {}
        // return Promise.reject(new Error('org_id or org_name is required'));
      },
      providesTags: (result, error, { org_id, org_name }) => [{ type: tags.organization, org_id: result?.organization?.id }],
      transformResponse: ({ data }, meta, arg) => {
        return transformOrganizationData(data);
      },
    }),
    createOrganization: builder.mutation<Organization, Omit<OrganizationWithoutRelations, 'id' | 'url_name'>>({
      query: (org) => {
        return {
          method: methods.POST,
          url: `/orgs`,
          body: org,
        };
      },
      invalidatesTags: (result, error, org) => [
        tags.currentUser,
        tags.organizationList,
      ],
      transformResponse: ({ data }, meta, arg) => (data as Organization),
    }),
    getOrganizationRoles: builder.query<{ items: OrganizationRole[] }, { org_id: Id }>({
      query: ({ org_id }) => {
        return {
          method: methods.GET,
          url: `/orgs/${org_id}/roles`,
        };
      },
      providesTags: (result, error, { org_id }) => [{ type: tags.organization, org_id }],
      transformResponse: ({ data }, meta, arg) => ({ items: data }),
    }),
    createOrganizationRole: builder.mutation<any, { org_id: Id, role: OrganizationRole }>({
      query: ({ org_id, role }) => {
        return {
          method: methods.POST,
          url: `/orgs/${org_id}/roles`,
          body: role,
        };
      },
      invalidatesTags: (result, error, { org_id }) => [
        { type: tags.organization, org_id },
        { type: tags.organizationUsers, org_id }
      ],
      transformResponse: ({ data }, meta, arg) => (data),
    }),
    deleteOrganization: builder.mutation<any, { org_id: Id }>({
      query: ({ org_id }) => {
        return {
          method: methods.DELETE,
          url: `/orgs/${org_id}`,
        };
      },
      invalidatesTags: (result, error, { org_id }) => [
        tags.currentUser,
        tags.organizationList,
        { type: tags.organization, org_id },
      ],
    }),
    hideOrganization: builder.mutation<any, { org_id: Id }>({
      query: ({ org_id }) => {
        return {
          method: methods.DELETE,
          url: `/orgs/${org_id}/hide`,
        };
      },
      invalidatesTags: (result, error, { org_id }) => [
        tags.currentUser,
        tags.organizationList,
        { type: tags.organization, org_id },
      ],
    }),
    unhideOrganization: builder.mutation<any, { org_id: Id }>({
      query: ({ org_id }) => {
        return {
          method: methods.POST,
          url: `/orgs/${org_id}/unhide`,
        };
      },
      invalidatesTags: (result, error, { org_id }) => [
        tags.currentUser,
        tags.organizationList,
        { type: tags.organization, org_id },
      ],
    }),

    updateOrganization: builder.mutation<Organization, { org_id: Id, data: OrganizationUpdateRoute }>({
      query: ({ org_id, data }) => {
        return {
          method: methods.PUT,
          url: `/orgs/${org_id}`,
          body: data,
        };
      },
      invalidatesTags: (result, error, { org_id }) => [
        tags.organizationList,
        { type: tags.organization, org_id },
      ],
      transformResponse: ({ data }, meta, arg) => data,
    }),
    /**
     * @deprecated
     */
    updateOrganizationDeprecated: builder.mutation<any, { org_id: Id, data: any }>({
      query: ({ org_id, data }) => {
        return {
          method: methods.PUT,
          url: `/orgs/${org_id}`,
          body: data,
        };
      },
      invalidatesTags: (result, error, { org_id }) => [
        tags.currentUser,
        tags.organizationList,
        { type: tags.organization, org_id },
      ],
    }),
    updateOrganizationUrlName: builder.mutation<any, { org_id: Id, url_name: string }>({
      query: ({ org_id, url_name }) => {
        return {
          method: methods.PUT,
          url: `/orgs/${org_id}/url_name`,
          body: {
            url_name
          },
        };
      },
      invalidatesTags: (result, error, { org_id }) => [
        tags.organizationList,
        { type: tags.organization, org_id },
      ],
      transformErrorResponse: (error: BaseAPIError) => error,
    }),
    setDefaultOrganizationRole: builder.mutation<any, { roleName: string, org_id: Id }>({
      query: ({ org_id, roleName }) => {
        return {
          method: methods.PUT,
          url: `/orgs/${org_id}/defaultRole/${roleName}`,
        };
      },
      invalidatesTags: (result, error, { org_id }) => [
        { type: tags.organization, org_id },
      ],
    }),
    updateOrganizationRole: builder.mutation<any, { org_id: Id, role_id: Id, role: OrganizationRole }>({
      query: ({ org_id, role_id, role }) => {
        return {
          method: methods.PUT,
          url: `/orgs/${org_id}/roles/${role_id}`,
          body: role,
        };
      },
      invalidatesTags: (result, error, { org_id }) => [
        tags.currentUser,
        { type: tags.organization, org_id },
        { type: tags.organizationUsers, org_id },
      ],
    }),
    deleteOrganizationRole: builder.mutation<any, { org_id: Id, role_id: Id }>({
      query: ({ org_id, role_id }) => {
        return {
          method: methods.DELETE,
          url: `/orgs/${org_id}/roles/${role_id}`,
        };
      },
      invalidatesTags: (result, error, { org_id }) => [{ type: tags.organization, org_id }],
    }),

    getOrganizationKeyvisualList: builder.query<KeyvisualList, { org_id?: Id }>({
      query: ({ org_id }) => {
        const org_prefix = org_id ? `/orgs/${org_id}` : ''
        const url = `${org_prefix}/keyvisuals`
        return {
          method: methods.GET,
          url,
        };
      },
      providesTags: (result, error, { org_id }) => [{ type: tags.organization, org_id }],
      transformResponse: ({ data }, meta, arg) => data,
    }),
    setOrganizationKeyvisual: builder.mutation<{ path: string }, { org_id: Id, path: string }>({
      query: ({ org_id, path }) => {
        return {
          method: methods.PUT,
          url: `/orgs/${org_id}/keyvisuals`,
          body: {
            path
          },
        };
      },
      invalidatesTags: (result, error, { org_id }) => [{ type: tags.organization, org_id }],
      transformResponse: ({ data }, meta, arg) => data,
    }),
    deleteOrganizationKeyvisual: builder.mutation<{ path: string, previousKeyvisuals: string[] }, { org_id: Id, path: string }>({
      query: ({ org_id, path }) => {
        return {
          method: methods.DELETE,
          url: `/orgs/${org_id}/keyvisuals`,
          body: {
            path
          },
        };
      },
      invalidatesTags: (result, error, { org_id }) => [{ type: tags.organization, org_id }],
      transformResponse: ({ data }, meta, arg) => data,
    }),

    uploadOrganizationLogo: builder.mutation<{ path: string }, { org_id: Id, file: FormData }>({
      query: ({ org_id, file }) => {
        return {
          uploadFile: true,
          method: methods.POST,
          url: `/orgs/${org_id}/upload/logo`,
          body: file,
        };
      },
      invalidatesTags: (result, error, { org_id }) => [{ type: tags.organization, org_id }, tags.organizationList],
      transformResponse: ({ data }, meta, arg) => data,
    }),
    uploadOrganizationKeyvisual: builder.mutation<{ path: string }, { org_id: Id, file: FormData }>({
      query: ({ org_id, file }) => {
        return {
          uploadFile: true,
          method: methods.POST,
          url: `/orgs/${org_id}/upload/keyvisual`,
          body: file,
        };
      },
      invalidatesTags: (result, error, { org_id }) => org_id ? [{ type: tags.organization, org_id }, tags.organizationList] : [tags.organization],
      transformResponse: ({ data }, meta, arg) => data,
    }),
    uploadAwardPicture: builder.mutation<{ path: string }, { org_id: Id, award_id: Id, file: FormData }>({
      query: ({ org_id, award_id, file }) => {
        return {
          uploadFile: true,
          method: methods.POST,
          url: `/orgs/upload/award_picture/${award_id}`,
          body: file,
        };
      },
      invalidatesTags: (result, error, { org_id }) => [{ type: tags.organization, org_id }],
      transformResponse: ({ data }, meta, arg) => data,
    }),
    uploadProductDetail: builder.mutation<{ path: string }, { org_id: Id, product_detail_id: Id, file: FormData }>({
      query: ({ org_id, product_detail_id, file }) => {
        return {
          uploadFile: true,
          method: methods.POST,
          url: `/orgs/upload/productDetail/${product_detail_id}`,
          body: file,
        };
      },
      invalidatesTags: (result, error, { org_id }) => [{ type: tags.organization, org_id }],
      transformResponse: ({ data }, meta, arg) => data,
    }),
    importOrganizations: builder.mutation<{ path: string }, { file: FormData, orgType?: Id }>({
      query: ({ file, orgType }) => {
        return {
          uploadFile: true,
          method: methods.POST,
          url: `/importOrgs${orgType ? `?organizationType=${orgType}` : ''}`,
          body: file,
        };
      },
      invalidatesTags: (result, error, { }) => [tags.organizationList, tags.currentUser],
      transformResponse: ({ data }, meta, params) => (data),
    }),
  }),

  overrideExisting: false,
})


// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
  useGetAllOrganizationTypesQuery,
  useGetOrganizationListQuery,
  useGetOrganizationQuery,
  useGetOrganizationTransformedQuery,
  useLazyGetOrganizationQuery,
  useLazyGetOrganizationTransformedQuery,

  useCreateOrganizationMutation,
  useDeleteOrganizationMutation,
  useHideOrganizationMutation,
  useUnhideOrganizationMutation,
  useUpdateOrganizationMutation,
  useUpdateOrganizationDeprecatedMutation,
  useUpdateOrganizationUrlNameMutation,
  useSetDefaultOrganizationRoleMutation,

  useGetOrganizationRolesQuery,
  useLazyGetOrganizationRolesQuery,
  useCreateOrganizationRoleMutation,
  useDeleteOrganizationRoleMutation,
  useUpdateOrganizationRoleMutation,

  useGetOrganizationKeyvisualListQuery,
  // useLazyGetKeyvisualListQuery,
  useSetOrganizationKeyvisualMutation,
  useDeleteOrganizationKeyvisualMutation,

  useUploadOrganizationLogoMutation,
  useUploadOrganizationKeyvisualMutation,
  useUploadAwardPictureMutation,
  useUploadProductDetailMutation,

  useImportOrganizationsMutation,
} = apiOrganization;
